// src/components/pages/PasswordReset.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import AppShell from "../layout/AppShell";
import Logo from "../layout/Logo";
import Button from "../ui/Button";
import { PasswordField } from "../ui/Field";
import Card from "../ui/Card";
import Icon from "../ui/Icon";

import axiosInstance from "../../utils/AxiosInstance";

/**
 * Final step of forgot-password. Receives { email, code } from /verification
 * and submits to:
 *   PUT /site/user/change-password/:email/:code   { password }
 */
export default function PasswordReset() {
  const location = useLocation();
  const navigate = useNavigate();

  const email = location.state?.email || "";
  const code = location.state?.code || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [seconds, setSeconds] = useState(5);

  useEffect(() => {
    if (!success) return;
    setSeconds(5);
    const id = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(id);
          navigate("/login");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [success, navigate]);

  const validate = () => {
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (/^(.)\1*$/.test(password)) return "Password can't be all the same character.";
    if (password !== confirm) return "Passwords don't match.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await axiosInstance.put(
        `/site/user/change-password/${encodeURIComponent(email)}/${encodeURIComponent(code)}`,
        { password }
      );
      if (res.status === 200) setSuccess(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!email || !code) {
    return (
      <AppShell bare>
        <div className="min-h-screen flex items-center justify-center px-4 py-12">
          <Card className="max-w-[420px] text-center">
            <Icon
              name="alert-circle"
              size={28}
              style={{ color: "var(--red)" }}
            />
            <h2
              className="font-display font-semibold mt-3 mb-2"
              style={{ fontSize: 22 }}
            >
              Reset link expired
            </h2>
            <p className="text-[13px] mb-5" style={{ color: "var(--txt-2)" }}>
              Please start the password reset flow again.
            </p>
            <Button full onClick={() => navigate("/login")}>
              Back to login
            </Button>
          </Card>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell bare>
      <div className="min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-[420px]">
          <div className="flex justify-center mb-6">
            <Logo size="lg" onClick={() => navigate("/")} />
          </div>

          <Card>
            <h1
              className="font-display font-semibold mb-1.5"
              style={{ fontSize: 24, color: "var(--txt)" }}
            >
              Set a new password.
            </h1>
            <p className="text-[13px] mb-5" style={{ color: "var(--txt-2)" }}>
              For account: <span style={{ color: "var(--txt)" }}>{email}</span>
            </p>

            {error && (
              <div
                className="mb-4 p-3 rounded-[10px] text-[13px] flex items-start gap-2"
                style={{
                  background: "var(--red-dim)",
                  border: "1px solid var(--red-border)",
                  color: "var(--red)",
                }}
              >
                <Icon name="alert-circle" size={16} style={{ marginTop: 1 }} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <PasswordField
                label="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                showStrength
                hint="At least 8 characters."
                autoComplete="new-password"
              />
              <PasswordField
                label="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
              <Button type="submit" full loading={submitting}>
                {submitting ? "Saving..." : "Save new password"}
              </Button>
            </form>
          </Card>
        </div>
      </div>

      {success && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center px-4"
          style={{ background: "rgba(7,9,15,0.85)", backdropFilter: "blur(8px)" }}
        >
          <Card className="max-w-[380px] text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{
                background: "var(--teal-dim)",
                border: "1px solid var(--teal-border)",
                color: "var(--teal)",
                animation: "checkPop 0.5s cubic-bezier(.175,.885,.32,1.275) forwards",
              }}
            >
              <Icon name="check" size={28} />
            </div>
            <h2
              className="font-display font-semibold mb-2"
              style={{ fontSize: 22, color: "var(--txt)" }}
            >
              Password updated.
            </h2>
            <p className="text-[13px] mb-5" style={{ color: "var(--txt-2)" }}>
              Redirecting to log in in {seconds}s...
            </p>
            <Button full onClick={() => navigate("/login")}>
              Log in now
            </Button>
          </Card>
        </div>
      )}
    </AppShell>
  );
}
